import {
    deleteSavedReport,
    listSavedReports,
    updateSavedReport,
    upgradeSavedReport,
    type SavedReport,
} from '@api/savedReports';
import {
    ActionIcon,
    Badge,
    Button,
    Card,
    Group,
    Loader,
    Modal,
    SimpleGrid,
    Stack,
    Text,
    TextInput,
    Textarea,
    Tooltip,
} from '@mantine/core';
import {
    ArrowClockwise,
    ArrowUp,
    BookmarkSimple,
    ChartBar,
    ChartLine,
    FileText,
    Gauge,
    GitDiff,
    type Icon,
    MagnifyingGlass,
    PencilSimple,
    Receipt,
    Table,
    Trash,
} from '@phosphor-icons/react';
import { extractApiErrorMessage } from '@utils/api-error';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import styles from './SavedReportsLibrary.module.css';

const TEMPLATE_LABELS: Record<string, string> = {
    'top-n-by-dimension': 'Top-N',
    'kpi-scorecard': 'KPI scorecard',
    'time-series': 'Time-series',
    pivot: 'Pivot',
    variance: 'Variance',
    'open-purchase-orders': 'Open POs',
};

const TEMPLATE_ICONS: Record<string, Icon> = {
    'top-n-by-dimension': ChartBar,
    'kpi-scorecard': Gauge,
    'time-series': ChartLine,
    pivot: Table,
    variance: GitDiff,
    'open-purchase-orders': Receipt,
};

function templateLabel(templateId: string | null | undefined): string {
    if (!templateId) return 'Dynamic';
    return TEMPLATE_LABELS[templateId] ?? templateId;
}

function templateIcon(templateId: string | null | undefined): Icon {
    if (!templateId) return FileText;
    return TEMPLATE_ICONS[templateId] ?? FileText;
}

/**
 * Slice 8.9.1 Fix X: how many days before a saved report's frozen
 * endDate counts as "stale" — i.e. clicking Run will query an
 * outdated window the user may have forgotten about. 7 days is a
 * reasonable default for "current data" semantics; tune later if
 * smoke shows the hint is too noisy or too rare.
 */
const STALE_DATE_RANGE_DAYS = 7;

/**
 * Extracts the saved report's `endDate` value if one exists.
 * Templates carry it on `report.params.endDate`; freeform carries
 * it on `report.freeformSpec.params.endDate`. Point-in-time saved
 * reports (e.g. "In-transit SKUs by PO") have no endDate at all
 * — returns null so the staleness check skips them entirely.
 */
function extractSavedEndDate(report: SavedReport): string | null {
    const fromTemplate = report.params?.endDate;
    if (typeof fromTemplate === 'string' && fromTemplate.length > 0) {
        return fromTemplate;
    }
    const fromFreeform = report.freeformSpec?.params?.endDate;
    if (typeof fromFreeform === 'string' && fromFreeform.length > 0) {
        return fromFreeform;
    }
    return null;
}

/**
 * True when the saved report has a frozen `endDate` AND that date
 * is more than STALE_DATE_RANGE_DAYS old. Re-running a stale saved
 * report queries the original window — not "today minus N days" —
 * which is almost always not what the user expects months later.
 * Returns false for point-in-time reports (no endDate) so they
 * never show the hint.
 */
function isDateRangeStale(report: SavedReport): boolean {
    const endDate = extractSavedEndDate(report);
    if (!endDate) return false;
    const parsed = new Date(endDate);
    if (Number.isNaN(parsed.getTime())) return false;
    const staleThresholdMs = STALE_DATE_RANGE_DAYS * 24 * 60 * 60 * 1000;
    return Date.now() - parsed.getTime() > staleThresholdMs;
}

function formatLastRun(value: string | null | undefined): string {
    if (!value) return 'Never run';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Never run';
    const now = Date.now();
    const diffMs = now - date.getTime();
    const min = 60 * 1000;
    const hour = 60 * min;
    const day = 24 * hour;
    if (diffMs < min) return 'just now';
    if (diffMs < hour) return `${Math.floor(diffMs / min)}m ago`;
    if (diffMs < day) return `${Math.floor(diffMs / hour)}h ago`;
    if (diffMs < 7 * day) return `${Math.floor(diffMs / day)}d ago`;
    return date.toLocaleDateString();
}

export default function SavedReportsLibraryContainer() {
    const navigate = useNavigate();
    const [reports, setReports] = useState<SavedReport[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [editing, setEditing] = useState<SavedReport | null>(null);
    const [confirmDelete, setConfirmDelete] = useState<SavedReport | null>(
        null,
    );

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const list = await listSavedReports();
            setReports(list);
        } catch (err) {
            setError(extractApiErrorMessage(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void refresh();
    }, [refresh]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return reports;
        return reports.filter((r) => {
            const haystack = [r.name, r.description ?? '', r.templateId]
                .join(' ')
                .toLowerCase();
            return haystack.includes(q);
        });
    }, [reports, search]);

    const onRun = useCallback(
        (report: SavedReport) => {
            // Navigates to the chat surface and auto-triggers the re-run via
            // the `?run=<id>&name=<name>` query params picked up by
            // ReportsContainer. Name is used for the chat thread's user-side
            // bubble label so it reads like "▶ <report name>".
            const params = new URLSearchParams({
                run: report.id,
                name: report.name,
            });
            navigate(`/reports?${params.toString()}`);
        },
        [navigate],
    );

    const onUpgrade = useCallback(async (report: SavedReport) => {
        try {
            const updated = await upgradeSavedReport(report.id);
            setReports((prev) =>
                prev.map((r) => (r.id === updated.id ? updated : r)),
            );
        } catch (err) {
            setError(extractApiErrorMessage(err));
        }
    }, []);

    const onDeleteConfirmed = useCallback(async () => {
        if (!confirmDelete) return;
        try {
            await deleteSavedReport(confirmDelete.id);
            setReports((prev) => prev.filter((r) => r.id !== confirmDelete.id));
            setConfirmDelete(null);
        } catch (err) {
            setError(extractApiErrorMessage(err));
        }
    }, [confirmDelete]);

    const onSaveEdit = useCallback(
        async (name: string, description: string) => {
            if (!editing) return;
            try {
                const updated = await updateSavedReport(editing.id, {
                    name,
                    description: description.trim() === '' ? null : description,
                });
                setReports((prev) =>
                    prev.map((r) => (r.id === updated.id ? updated : r)),
                );
                setEditing(null);
            } catch (err) {
                setError(extractApiErrorMessage(err));
            }
        },
        [editing],
    );

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                    <div>
                        <h1 className={styles.headerTitle}>Saved reports</h1>
                        <div className={styles.headerSubtitle}>
                            Reports you've saved from the chat. Run, rename, or
                            delete from here.
                        </div>
                    </div>
                    <Button
                        variant="subtle"
                        size="xs"
                        leftSection={<MagnifyingGlass size={14} />}
                        onClick={() => navigate('/reports')}
                    >
                        New report
                    </Button>
                </Group>
            </div>

            <div className={styles.toolbar}>
                <TextInput
                    placeholder="Search by name, description, or template..."
                    value={search}
                    onChange={(e) => setSearch(e.currentTarget.value)}
                    leftSection={<MagnifyingGlass size={14} />}
                    w={400}
                />
                <ActionIcon
                    variant="subtle"
                    onClick={() => void refresh()}
                    aria-label="Refresh"
                >
                    <ArrowClockwise size={16} />
                </ActionIcon>
            </div>

            {error && <div className={styles.errorBanner}>{error}</div>}

            <div className={styles.body}>
                {loading ? (
                    <div className={styles.empty}>
                        <Loader size="sm" />
                        <Text size="sm">Loading…</Text>
                    </div>
                ) : filtered.length === 0 ? (
                    <EmptyState
                        hasSearch={search.length > 0}
                        onCreate={() => navigate('/reports')}
                    />
                ) : (
                    <SimpleGrid
                        cols={{ base: 1, sm: 2, md: 3, lg: 4 }}
                        spacing="sm"
                    >
                        {filtered.map((report) => (
                            <ReportCard
                                key={report.id}
                                report={report}
                                onRun={() => onRun(report)}
                                onEdit={() => setEditing(report)}
                                onDelete={() => setConfirmDelete(report)}
                                onUpgrade={() => void onUpgrade(report)}
                            />
                        ))}
                    </SimpleGrid>
                )}
            </div>

            <EditModal
                report={editing}
                onClose={() => setEditing(null)}
                onSave={onSaveEdit}
            />
            <DeleteModal
                report={confirmDelete}
                onClose={() => setConfirmDelete(null)}
                onConfirm={onDeleteConfirmed}
            />
        </div>
    );
}

function ReportCard({
    report,
    onRun,
    onEdit,
    onDelete,
    onUpgrade,
}: {
    report: SavedReport;
    onRun: () => void;
    onEdit: () => void;
    onDelete: () => void;
    onUpgrade: () => void;
}) {
    const TemplateIcon = templateIcon(report.templateId);
    return (
        <Card withBorder padding="sm" radius="md" className={styles.card}>
            <div className={styles.cardTop}>
                <div className={styles.cardIdentity}>
                    <div className={styles.iconTile}>
                        <TemplateIcon size={16} weight="duotone" />
                    </div>
                    <span className={styles.templateLabel}>
                        {templateLabel(report.templateId)}
                    </span>
                </div>
                <div className={styles.cardActions}>
                    <Tooltip label="Edit">
                        <ActionIcon
                            size="sm"
                            variant="subtle"
                            color="gray"
                            onClick={onEdit}
                            aria-label="Edit"
                        >
                            <PencilSimple size={14} />
                        </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Delete">
                        <ActionIcon
                            size="sm"
                            variant="subtle"
                            color="red"
                            onClick={onDelete}
                            aria-label="Delete"
                        >
                            <Trash size={14} />
                        </ActionIcon>
                    </Tooltip>
                </div>
            </div>

            <Text fw={600} size="sm" lineClamp={2}>
                {report.name}
            </Text>

            {report.description && (
                <Text size="xs" c="dimmed" lineClamp={2}>
                    {report.description}
                </Text>
            )}

            {/* Slice 8.9.1 Fix X: stale date-range hint. Frozen date
                ranges don't roll forward on Run — when the saved
                endDate is more than 7d old, surface the gotcha so
                the user knows what they're about to query. */}
            {isDateRangeStale(report) && (
                <Text
                    size="xs"
                    className={styles.staleDateHint}
                >
                    Date range from when saved · click Run to use it as-is
                </Text>
            )}

            {report.availableUpgradeVersion != null && (
                <Tooltip
                    label={`Upgrade to v${report.availableUpgradeVersion}`}
                >
                    <Badge
                        size="sm"
                        variant="light"
                        color="orange"
                        leftSection={<ArrowUp size={10} />}
                        onClick={onUpgrade}
                        className={styles.upgradeBadge}
                    >
                        v{report.availableUpgradeVersion} available
                    </Badge>
                </Tooltip>
            )}

            <div className={styles.cardFooter}>
                <span className={styles.lastRun}>
                    Last run: {formatLastRun(report.lastRunAt)}
                </span>
                <Button
                    variant="light"
                    className={styles.runButton}
                    onClick={onRun}
                >
                    Run
                </Button>
            </div>
        </Card>
    );
}

function EmptyState({
    hasSearch,
    onCreate,
}: {
    hasSearch: boolean;
    onCreate: () => void;
}) {
    if (hasSearch) {
        return (
            <div className={styles.empty}>
                <Text size="sm" c="dimmed">
                    No saved reports match your search.
                </Text>
            </div>
        );
    }
    return (
        <div className={styles.empty}>
            <BookmarkSimple size={32} weight="duotone" />
            <Text size="sm" fw={500}>
                No saved reports yet
            </Text>
            <Text size="xs" c="dimmed">
                Run a report in chat, then click "Save this report" — it'll show
                up here.
            </Text>
            <Button size="xs" mt="sm" onClick={onCreate}>
                Go to chat
            </Button>
        </div>
    );
}

function EditModal({
    report,
    onClose,
    onSave,
}: {
    report: SavedReport | null;
    onClose: () => void;
    onSave: (name: string, description: string) => Promise<void>;
}) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (report) {
            setName(report.name);
            setDescription(report.description ?? '');
        }
    }, [report]);

    const onSubmit = async () => {
        if (!name.trim()) return;
        setBusy(true);
        try {
            await onSave(name.trim(), description);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            opened={report != null}
            onClose={onClose}
            title="Edit saved report"
            size="md"
            centered
        >
            <Stack gap="sm">
                <TextInput
                    label="Name"
                    value={name}
                    onChange={(e) => setName(e.currentTarget.value)}
                    maxLength={200}
                    required
                />
                <Textarea
                    label="Description"
                    description="Optional. Helps you remember what this report is for."
                    value={description}
                    onChange={(e) => setDescription(e.currentTarget.value)}
                    maxLength={1000}
                    autosize
                    minRows={2}
                    maxRows={6}
                />
                <Group justify="flex-end" gap="xs">
                    <Button variant="subtle" onClick={onClose} disabled={busy}>
                        Cancel
                    </Button>
                    <Button
                        onClick={onSubmit}
                        loading={busy}
                        disabled={!name.trim()}
                    >
                        Save
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
}

function DeleteModal({
    report,
    onClose,
    onConfirm,
}: {
    report: SavedReport | null;
    onClose: () => void;
    onConfirm: () => Promise<void>;
}) {
    const [busy, setBusy] = useState(false);
    const onClick = async () => {
        setBusy(true);
        try {
            await onConfirm();
        } finally {
            setBusy(false);
        }
    };
    return (
        <Modal
            opened={report != null}
            onClose={onClose}
            title="Delete saved report"
            size="sm"
            centered
        >
            <Stack gap="sm">
                <Text size="sm">
                    Are you sure you want to delete{' '}
                    <Text span fw={600}>
                        {report?.name}
                    </Text>
                    ? This can't be undone.
                </Text>
                <Group justify="flex-end" gap="xs">
                    <Button variant="subtle" onClick={onClose} disabled={busy}>
                        Cancel
                    </Button>
                    <Button color="red" onClick={onClick} loading={busy}>
                        Delete
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
}
